import { Component } from '@angular/core';
import { OnInit, Input, ViewChild } from '@angular/core';
import { NgbActiveModal } from '@ng-bootstrap/ng-bootstrap';
import { CitationService } from '../../services/citation.service'; // Legacy libraryService
import { UserService } from '../../services/user.service';
import { ProductService } from '../../services/product.service';
import dayjs from 'dayjs';

/**
 * Component for creating bibliographic citations for selected products.
 */
@Component({
	selector: 'citation',
	templateUrl: './citation.component.html',
	styleUrl: './citation.component.css'
})
export class CitationComponent implements OnInit {
	@Input() productGroup: any;
	@ViewChild('citationHolder') citationHolder: any;

	LOCALIZATION: any;
	styles: any[] = [];
	citationForm = {
		lang: "en",
		style: "apa"
	};
	citations: any[] = [];
	citation: any;
	ready: boolean = false;

	constructor(
		private citationService: CitationService,
		private userService: UserService,
		private productService: ProductService,
		private ngbmodalService: NgbActiveModal
	) { }

	ngOnInit(): void {
		this.LOCALIZATION = this.userService.LOCALIZATION;

		this.styles = this.citationService.supportedFormats;
		this.generateCitations();
	}

	/**
	 * Creates citations for the products.
	 */
	generateCitations(): void {
		let citationData: { [productId: string]: { [key: string]: any } } = {};
		let productIds: string[] = [];

		// Iterate over the product group
		for (const product of this.productGroup) {
			let citationItems: { [key: string]: any } = {}; // Removed extra semicolon

			if (this.citationService.mapping) {
				for (const key in this.citationService.mapping) {
					if (product[key]) {
						let value = product[key];
						// Handle the mapping type cases
						switch (this.citationService.mapping[key].type) {
							case "date":
								try {
									let date = dayjs(value);
									// Add 1 to month() because months are 0-indexed
									value = { "date-parts": [[date.year(), date.month() + 1, date.date()]] };
								} catch (e) {
									value = "";  // Assign empty string in case of error
								}
								break;

							case "entity":
								value = [{ "family": value }];
								break;

							default:
								console.warn(`Unhandled mapping type: ${this.citationService.mapping[key].type}`);
								break;
						}
						citationItems[this.citationService.mapping[key].name] = value;
					}
				}

				citationData[product.id] = citationItems;
				productIds.push(product.id);
			}
		}

		// Fetch the purls, if available
		this.productService.getBatchProductShareLinks(productIds).then(
			(links: any) => {
				const purlKey = this.citationService.mapping["url"] ? this.citationService.mapping["url"].name : "URL";

				for (const key in links) {
					if (citationData[key]) {
						citationData[key][purlKey] = links[key]["ITEM"];
					}
				}

				this.getCitation(citationData, productIds);
			},
			(err: any) => {
				console.error('Error fetching product links:', err);
				this.getCitation(citationData, productIds);
			}
		);
	}

	getCitation(citationData: any = {}, productIds: string[]): void {
		this.citationService.createBibliography(citationData, this.citationForm.lang, this.citationForm.style, productIds).then(
			(response: any) => {
				this.citations = response[1];
				this.ready = true;
			},
			(err: any) => {
				console.error(err);
			}
		);
	}

	/**
	 * Copies the citation data to the clipboard.
	 */
	copyToClipboard(): void {
		// Class comes from citeproc.js library's citation generation.
		const elements = this.citationHolder.nativeElement.querySelectorAll('.csl-entry');

		let totalText = '';
		elements.forEach((element: HTMLElement) => {
			totalText += element.textContent?.trim() + '\n';
		});

		totalText = totalText.trim();

		// Create a temporary textarea element to hold the text
		const tempTextArea = document.createElement('textarea');
		document.body.appendChild(tempTextArea);
		tempTextArea.value = totalText;
		tempTextArea.select();
		tempTextArea.setSelectionRange(0, totalText.length); // For mobile devices

		try {
			const successful = document.execCommand('copy');
			if (successful) {
				console.log('Text copied to clipboard');
			} else {
				console.error('Failed to copy text');
			}
		} catch (err) {
			console.error('Error while copying text: ', err);
		}

		document.body.removeChild(tempTextArea);
	}

	/**
	 * Prints the citation data.
	 */
	print(): void {
		let printContents = this.citationHolder.nativeElement.innerHTML;
		const printWin = window.open('', '_blank');
		if (printWin) {
			printWin.document.open();
			printWin.document.write(`
		    <!DOCTYPE html>
		    <html>
		    <head>
			  <title>Print Citations</title>
			</head>
		    <body onload="window.print();">
		      ${printContents}
		    </body>
		    </html>
		  `);
			printWin.document.close();
		}
	}

	/**
	 * Closes the modal.
	 */
	close(): void {
		this.ngbmodalService.dismiss();
	}
}
